import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Clock, 
  TrendingUp, 
  Users, 
  Briefcase,
  ChevronRight,
  Download,
  Calendar,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { format, subDays } from "date-fns";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useVisibilityOverride } from "../context/VisibilityOverrideContext";
import { downloadFile } from "../lib/download";
import DataTable from "../components/DataTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  feeEarning: "#10B981",
  nonFeeEarning: "#F59E0B",
  utilization: "#6366F1",
  projects: ["#EC4899", "#8B5CF6", "#06B6D4", "#F97316", "#84CC16"]
};

const FEE_BREAKDOWN_COLORS = {
  "Fee Earning Delivery": "#6366F1",
  "Fee Earning Prep": "#10B981",
  "Non-Fee Earning Delivery": "#F59E0B",
  "Non-Fee Earning Prep": "#EF4444",
};
const PIE_COLORS_FALLBACK = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { includeExcluded } = useVisibilityOverride();
  const canDrillDown = role === "admin" || role === "director";
  const [stats, setStats] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [feeBreakdown, setFeeBreakdown] = useState([]);
  const [projectHours, setProjectHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Session-only filters
  const [filterOptions, setFilterOptions] = useState({ consultants: [], projects: [], consultants_all: [], projects_all: [], excluded_personnel: [], excluded_project_ids: [] });
  const [selectedConsultants, setSelectedConsultants] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
      const endDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";

      const qs = new URLSearchParams();
      if (startDate && endDate) {
        qs.set("start_date", startDate);
        qs.set("end_date", endDate);
      }
      if (selectedConsultants.length > 0) {
        qs.set("consultants", selectedConsultants.join(","));
      }
      if (selectedProjects.length > 0) {
        qs.set("projects", selectedProjects.join(","));
      }
      if (includeExcluded) {
        qs.set("include_excluded", "true");
      }
      const params = qs.toString() ? `?${qs.toString()}` : "";

      const [statsRes, consultantsRes, feeRes, projectRes, filterRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats${params}`),
        axios.get(`${API}/consultants${params}`),
        axios.get(`${API}/analytics/fee-earning-breakdown${params}`),
        axios.get(`${API}/analytics/project-hours${params}`),
        axios.get(`${API}/filter-options${startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : ""}`),
      ]);

      // Initialize filter selections ONLY on first load
      // Show ALL options in dropdowns, but pre-select only non-excluded ones
      const opts = filterRes.data || { consultants: [], projects: [], consultants_all: [], projects_all: [], excluded_personnel: [], excluded_project_ids: [] };
      setFilterOptions(opts);
      if (!filtersInitialized && (opts.consultants_all?.length || opts.consultants?.length)) {
        // Pre-select non-excluded items (admin defaults applied)
        setSelectedConsultants(opts.consultants || []);
        setSelectedProjects(opts.projects || []);
        setFiltersInitialized(true);
      }

      setStats(statsRes.data);
      setConsultants(consultantsRes.data?.items || consultantsRes.data || []);
      setFeeBreakdown(feeRes.data);
      setProjectHours(projectRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedConsultants, selectedProjects]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleExport = async (type = "csv") => {
    try {
      const startDate = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
      const endDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
      const params = startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : "";
      
      if (type === "pdf") {
        await downloadFile(`${API}/export/pdf/dashboard${params}`);
      } else {
        await downloadFile(`${API}/export/csv${params}`);
      }
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export");
    }
  };

  const toggleAllConsultants = (checked) => {
    if (checked) setSelectedConsultants(filterOptions.consultants_all || filterOptions.consultants || []);
    else setSelectedConsultants([]);
  };

  const toggleConsultant = (name, checked) => {
    setSelectedConsultants((prev) => {
      const s = new Set(prev);
      if (checked) s.add(name);
      else s.delete(name);
      return Array.from(s);
    });
  };

  const toggleAllProjects = (checked) => {
    if (checked) setSelectedProjects(filterOptions.projects_all || filterOptions.projects || []);
    else setSelectedProjects([]);
  };

  const toggleProject = (pid, checked) => {
    setSelectedProjects((prev) => {
      const s = new Set(prev);
      if (checked) s.add(pid);
      else s.delete(pid);
      return Array.from(s);
    });
  };


  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const hasData = stats && stats.total_hours > 0;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Timesheet Analytics</h1>
          <p className="text-muted-foreground mt-1">Track consultant utilization and project hours</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <input
                type="date"
                value={dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateRange((prev) => ({ ...prev, from: v ? new Date(v + "T00:00:00") : null }));
                }}
                className="h-9 w-36 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="date-from"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <input
                type="date"
                value={dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateRange((prev) => ({ ...prev, to: v ? new Date(v + "T00:00:00") : null }));
                }}
                className="h-9 w-36 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="date-to"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setDateRange({ from: null, to: null })}
              disabled={!dateRange.from && !dateRange.to}
              data-testid="date-clear"
            >
              Clear
            </Button>
          </div>
          <Button onClick={() => handleExport("csv")} disabled={!hasData} variant="outline" className="gap-2" data-testid="export-csv-btn">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">CSV</span>
          </Button>
          <Button onClick={() => handleExport("pdf")} disabled={!hasData} className="gap-2" data-testid="export-pdf-btn">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </Button>

          {/* Session-only filters */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="consultant-filter-btn">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Consultants</span>
                <Badge variant="secondary" className="ml-1">
                  {selectedConsultants.length}/{(filterOptions.consultants_all || filterOptions.consultants || []).length}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 max-h-96 overflow-auto" align="end">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Consultants</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllConsultants(selectedConsultants.length !== (filterOptions.consultants_all || filterOptions.consultants || []).length)}
                  >
                    {selectedConsultants.length === (filterOptions.consultants_all || filterOptions.consultants || []).length ? "Clear" : "Select all"}
                  </Button>
                </div>
                <div className="space-y-2">
                  {(filterOptions.consultants_all || filterOptions.consultants || []).map((c) => {
                    const isExcluded = (filterOptions.excluded_personnel || []).includes(c);
                    return (
                      <label key={c} className={`flex items-center justify-between text-sm ${isExcluded ? "opacity-60" : ""}`}>
                        <span className="truncate pr-2">{c}{isExcluded ? " (hidden)" : ""}</span>
                        <input
                          type="checkbox"
                          checked={selectedConsultants.includes(c)}
                          onChange={(e) => toggleConsultant(c, e.target.checked)}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="project-filter-btn">
                <Briefcase className="w-4 h-4" />
                <span className="hidden sm:inline">Projects</span>
                <Badge variant="secondary" className="ml-1">
                  {selectedProjects.length}/{(filterOptions.projects_all || filterOptions.projects || []).length}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 max-h-96 overflow-auto" align="end">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Projects</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllProjects(selectedProjects.length !== (filterOptions.projects_all || filterOptions.projects || []).length)}
                  >
                    {selectedProjects.length === (filterOptions.projects_all || filterOptions.projects || []).length ? "Clear" : "Select all"}
                  </Button>
                </div>
                <div className="space-y-2">
                  {(filterOptions.projects_all || filterOptions.projects || []).map((p) => {
                    const isExcluded = (filterOptions.excluded_project_ids || []).includes(p);
                    return (
                      <label key={p} className={`flex items-center justify-between text-sm ${isExcluded ? "opacity-60" : ""}`}>
                        <span className="truncate pr-2">{p}{isExcluded ? " (hidden)" : ""}</span>
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(p)}
                          onChange={(e) => toggleProject(p, e.target.checked)}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>

        </div>
      </div>

      {!hasData ? (
        <Card className="border-dashed" data-testid="no-data-card">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Timesheet Data</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Upload your timesheet Excel or CSV file to see analytics and insights about consultant utilization.
            </p>
            <Button onClick={() => navigate("/upload")} data-testid="upload-cta-btn">
              Upload Timesheet Data
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="metric-card" data-testid="total-hours-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{stats.total_hours.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card" data-testid="utilization-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization Rate</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{stats.overall_utilization_rate}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-indigo-500/10">
                    <TrendingUp className="w-5 h-5 text-indigo-500" />
                  </div>
                </div>
                <Progress value={stats.overall_utilization_rate} className="mt-4 h-2" />
              </CardContent>
            </Card>

            <Card className="metric-card" data-testid="consultants-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Consultants</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{stats.total_consultants}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-pink-500/10">
                    <Users className="w-5 h-5 text-pink-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card" data-testid="projects-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Projects</p>
                    <p className="text-3xl font-bold text-foreground tabular-nums">{stats.total_projects}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10">
                    <Briefcase className="w-5 h-5 text-cyan-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Fee Earning Pie Chart - Clickable */}
            <Card data-testid="fee-breakdown-chart">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Fee Earning Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 30, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={feeBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                        onClick={canDrillDown ? () => navigate("/analytics") : undefined}
                        style={{ cursor: canDrillDown ? "pointer" : "default" }}
                      >
                        {feeBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={FEE_BREAKDOWN_COLORS[entry.name] || PIE_COLORS_FALLBACK[index % PIE_COLORS_FALLBACK.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`${value} hours`, ""]}
                        contentStyle={{ 
                          backgroundColor: "hsl(var(--card))", 
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px"
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Project Hours Bar Chart - Clickable */}
            <Card data-testid="project-hours-chart">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Hours by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={projectHours} 
                      layout="vertical" 
                      margin={{ left: 20, right: 20 }}
                      onClick={canDrillDown ? () => navigate("/analytics") : undefined}
                      style={{ cursor: canDrillDown ? "pointer" : "default" }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: "hsl(var(--card))", 
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px"
                        }}
                      />
                      <Legend />
                      <Bar dataKey="fee_earning" name="Fee Earning" fill={COLORS.feeEarning} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="non_fee_earning" name="Non-Fee Earning" fill={COLORS.nonFeeEarning} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Consultants List - Admin/Director only */}
          {canDrillDown && (
          <Card data-testid="consultants-list">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Consultants Overview</CardTitle>
              <p className="text-sm text-muted-foreground">Click to view details</p>
            </CardHeader>
            <CardContent>
              <DataTable
                fetchData={async ({ page, pageSize, sort, order, search }) => {
                  const qs = new URLSearchParams();
                  const startDate = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
                  const endDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
                  if (startDate && endDate) { qs.set("start_date", startDate); qs.set("end_date", endDate); }
                  if (selectedConsultants.length > 0) qs.set("consultants", selectedConsultants.join(","));
                  if (selectedProjects.length > 0) qs.set("projects", selectedProjects.join(","));
                  qs.set("page", page); qs.set("page_size", pageSize);
                  qs.set("sort", sort); qs.set("order", order);
                  if (search) qs.set("search", search);
                  const res = await axios.get(`${API}/consultants?${qs.toString()}`);
                  return res.data;
                }}
                columns={[
                  {
                    key: "personnel", label: "Consultant",
                    render: (row) => (
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {(row.personnel || "").split(" ").map(n => n?.[0] || "").join("")}
                          </span>
                        </div>
                        <span className="font-medium text-foreground">{row.personnel}</span>
                      </div>
                    ),
                  },
                  { key: "total_hours", label: "Total Hours", cellClassName: "tabular-nums font-medium" },
                  {
                    key: "fee_earning_hours", label: "Fee Earning",
                    render: (row) => <Badge className="badge-fee-earning">{row.fee_earning_hours}h</Badge>,
                  },
                  {
                    key: "non_fee_earning_hours", label: "Non-Fee Earning",
                    render: (row) => <Badge className="badge-non-fee-earning">{row.non_fee_earning_hours}h</Badge>,
                  },
                  {
                    key: "utilization_rate", label: "Utilization",
                    render: (row) => (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${row.utilization_rate}%`, backgroundColor: row.utilization_rate >= 50 ? COLORS.feeEarning : COLORS.nonFeeEarning }} />
                        </div>
                        <span className="text-sm tabular-nums">{row.utilization_rate}%</span>
                      </div>
                    ),
                  },
                  { key: "projects_count", label: "Projects", cellClassName: "tabular-nums" },
                  {
                    key: "_action", label: "", sortable: false,
                    render: () => <ChevronRight className="w-4 h-4 text-muted-foreground" />,
                  },
                ]}
                defaultSort="total_hours"
                defaultOrder="desc"
                searchPlaceholder="Search consultants..."
                onRowClick={(row) => navigate(`/consultant/${encodeURIComponent(row.personnel)}`)}
                refreshKey={`${dateRange.from}-${dateRange.to}-${selectedConsultants.length}-${selectedProjects.length}`}
                emptyMessage="No consultant data found"
              />
            </CardContent>
          </Card>
          )}
        </>
      )}
    </div>
  );
}
